import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material'
import React, { useMemo, useState } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { useGetAllInventoryPartsQuery } from '../../../redux/api'
import { InventoryPartEntry, PartOverrideUnitOfMeasureEnum } from '../../../sdk'
import { getPartAvailableQuantity } from '../../../utils/convertFluidsUnits/convertFluidsUnits'
import { DialogDefaultActions } from '../../SharedUI/DialogDefaultActions'
import { PartAndType } from '../ServiceCard'
import { getDefaultQuantity } from './useClaimPartDialog'

export interface ClaimInventoryProps {
  partAndType: PartAndType
  inventoryPart: InventoryPartEntry
  partIds: string[]
}

export const ClaimPartForm = ({
  customProps,
  setCustomProps,
  handleClaim
}: {
  customProps: ClaimInventoryProps
  setCustomProps: (customProps: ClaimInventoryProps | undefined) => void
  handleClaim: (customProps: ClaimInventoryProps, hasCore: boolean) => void
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { data: shopInventory } = useGetAllInventoryPartsQuery(
    {
      authorization: accessToken,
      shop: shopId,
      stockedOnly: false,
      partId: customProps.partIds
    },
    { skip: customProps.partIds.length === 0 }
  )

  const [selectedPartId, setSelectedPartId] = useState<string>(
    customProps.partIds.length !== 0 ? customProps.partIds[0] : ''
  )

  const availableQuantity = useMemo(() => {
    if (!selectedPartId || !shopInventory) {
      return 0
    }
    return getPartAvailableQuantity(
      selectedPartId,
      shopInventory,
      customProps.partAndType.part.unitOfMeasure
    )
  }, [
    customProps.partAndType.part.unitOfMeasure,
    selectedPartId,
    shopInventory
  ])

  const [hasCore, setHasCore] = useState(false)
  const unitText = useMemo(() => {
    if (
      customProps.partAndType.part.unitOfMeasure ===
      PartOverrideUnitOfMeasureEnum.DiscreteUnit
    ) {
      return ''
    }
    return ` ${customProps.partAndType.part.unitOfMeasure}`
  }, [customProps.partAndType.part.unitOfMeasure])
  if (availableQuantity === 0) {
    return (
      <>
        <DialogContent>No parts available in inventory.</DialogContent>{' '}
        <DialogActions>
          <Button onClick={() => setCustomProps(undefined)}>Cancel</Button>
        </DialogActions>
      </>
    )
  }
  return (
    <>
      <DialogContent>
        {customProps.partAndType.type === 'inCart' &&
        customProps.partIds.length > 1 &&
        shopInventory ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Multiple parts found with same part number. Select the correct
              part:
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>{'Select Part'}</InputLabel>
                <Select
                  label="Select Part"
                  variant={'standard'}
                  value={selectedPartId}
                  onChange={(event) => {
                    setSelectedPartId(event.target.value)
                  }}
                  fullWidth>
                  {customProps.partIds.map((partId) => (
                    <MenuItem key={partId} value={partId}>
                      {shopInventory!.allParts![partId]!.info!.brand} -{' '}
                      {shopInventory!.allParts![partId]!.info!.partNumber}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Alert>

            <Typography>
              {`${availableQuantity + unitText} ${
                shopInventory!.allParts![selectedPartId]!.info!.name
              } ${
                availableQuantity === 1 ? 'is' : 'are'
              } available in the inventory.`}
            </Typography>
          </>
        ) : (
          <Typography>
            {`${availableQuantity + unitText} ${
              customProps.partAndType.part.partName
            } ${
              availableQuantity === 1 ? 'is' : 'are'
            } available in the inventory.`}
          </Typography>
        )}

        {availableQuantity === 0 && customProps.partIds.length === 0 && (
          <Typography>No parts available in inventory.</Typography>
        )}

        {availableQuantity !== 0 && (
          <>
            {customProps.partAndType.type === 'claimed' && (
              <Typography>
                Claim{' '}
                {getDefaultQuantity(
                  availableQuantity,
                  customProps.partAndType.part.need
                ) + unitText}{' '}
                from inventory?
              </Typography>
            )}
            {customProps.partAndType.type === 'standsAlone' && (
              <Typography>
                Claim{' '}
                {getDefaultQuantity(
                  availableQuantity,
                  customProps.partAndType.part.quantity
                ) + unitText}{' '}
                from inventory?
              </Typography>
            )}
            {customProps.partAndType.type === 'inCart' && (
              <>
                {availableQuantity >= customProps.partAndType.part.quantity ? (
                  <Typography>
                    Claim {customProps.partAndType.part.quantity} from inventory
                    and clear your cart?
                  </Typography>
                ) : (
                  <Typography>
                    Claim {availableQuantity} from inventory and change quantity
                    in your cart from {customProps.partAndType.part.quantity} to{' '}
                    {customProps.partAndType.part.quantity - availableQuantity}?
                  </Typography>
                )}
              </>
            )}
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasCore}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setHasCore(event.target.checked)
                    }}
                  />
                }
                label="Create core item?"
              />
            </FormGroup>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {availableQuantity === 0 ? (
          <Button onClick={() => setCustomProps(undefined)}>Cancel</Button>
        ) : (
          <DialogDefaultActions
            cancelCallback={() => setCustomProps(undefined)}
            saveCallback={handleClaim.bind(
              null,
              {
                partAndType: customProps.partAndType,
                inventoryPart: shopInventory!.allParts![selectedPartId]!,
                partIds: customProps.partIds
              },
              hasCore
            )}
            saveTextOverride="Claim"
          />
        )}
      </DialogActions>
    </>
  )
}
